import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  isOrderFeedSource,
  usePotentials,
  useRemovePotential,
  useVoucherCap,
} from '../basket/potentials'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { useAuthStore } from '../store/auth'
import { useAnyOverlayOpen } from '../store/overlay'
import { useSelectionStore } from '../store/selection'
import { GlyphShirt, IconArrowRight, IconChevronDown, IconX } from '../ui/icons'
import { useMoneyFmt } from '../util/useMoney'

interface MobileBasketProps {
  ctaLabel: string
  ctaShowArrow: boolean
  continueDisabled: boolean
  showBack: boolean
  onContinue: () => void
  onBack: () => void
}

/**
 * Mobile basket (redesign §"Mobile"): a fixed bottom bar showing the reward + count and the CTA,
 * that expands into a bottom sheet to view/remove items — the items are hidden until tapped. It's
 * portalled to the shadow-root overlay node (a sibling of `.app`) so its `position: fixed` resolves
 * against the viewport, not `.app`'s container-query box (matching the dialogs/toast). CSS shows it
 * only on narrow viewports; the desktop rail is hidden there.
 */
export function MobileBasket({
  ctaLabel,
  ctaShowArrow,
  continueDisabled,
  showBack,
  onContinue,
  onBack,
}: MobileBasketProps) {
  const container = usePortalContainer()
  const { potentials, reward, loading } = usePotentials()
  const { max, exceeded } = useVoucherCap()
  const { remove, removing } = useRemovePotential()
  const deselect = useSelectionStore((s) => s.deselect)
  const defaultImage = useAuthStore((s) => s.config.defaultImage)
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  const anyOverlayOpen = useAnyOverlayOpen()
  const [open, setOpen] = useState(false)
  // When the on-screen keyboard is up (a field is focused), we hide the basket entirely: otherwise
  // it stacks on top of the keyboard and the two eat most of the screen while typing (esp. the
  // details form). Detected from the visual-viewport shrink below.
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)

  // Mobile browsers anchor `position: fixed; bottom: 0` to the *layout* viewport, but the dynamic
  // address bar/toolbar sits in the gap up to the *visual* viewport — so on scroll-up the toolbar
  // overlaps our bar. Lift the sheet by that gap (self-correcting: 0 when there's no overlap).
  useEffect(() => {
    const vv = window.visualViewport
    const el = sheetRef.current
    if (!vv || !el) return
    const update = () => {
      // Total the visual viewport has shrunk vs the layout viewport: a large shrink (independent of
      // scroll) means the software keyboard is open; a small one is just the browser toolbar.
      const shrink = window.innerHeight - vv.height
      const keyboard = shrink > 150
      setKeyboardOpen(keyboard)
      // Toolbar-overlap lift only matters when the keyboard is closed (we hide the bar otherwise).
      const gap = shrink - vv.offsetTop
      el.style.transform = !keyboard && gap > 1 ? `translateY(${-gap}px)` : ''
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  const n = potentials.length
  const hasFeedItem = potentials.some((p) => isOrderFeedSource(p.sourceType))
  const countLabel = n === 1 ? t('rail.itemCount', { count: n }) : t('rail.itemCountPlural', { count: n })

  // Hide entirely while a dialog is open — no z-index guessing, the basket just isn't in the DOM.
  if (!container || anyOverlayOpen) return null

  const advance = () => {
    setOpen(false)
    onContinue()
  }

  return createPortal(
    // Kept mounted (the visual-viewport listener/ref must stay alive); hidden via inline style,
    // which beats the `.mbasket { display:block }` rule, while the keyboard is up.
    <div
      className="mbasket"
      data-open={open ? 'true' : 'false'}
      style={keyboardOpen ? { display: 'none' } : undefined}
    >
      {open && <div className="mbasket-scrim" onClick={() => setOpen(false)} />}

      <div className="mbasket-sheet" ref={sheetRef}>
        {open && (
          <div className="mbasket-list">
            {n === 0 ? (
              <div className="rail-empty">{loading ? t('rail.loading') : t('rail.empty')}</div>
            ) : (
              potentials.map((p) => {
                const image = p.imageUrls?.[0] || defaultImage
                const isFeed = isOrderFeedSource(p.sourceType)
                return (
                  <div className="rail-item" key={p._id}>
                    <span className="thumb" style={{ width: 38, height: 38 }}>
                      {image ? <img src={image} alt="" /> : <GlyphShirt />}
                    </span>
                    <span className="meta">
                      <div className="nm">
                        {p.name}
                      </div>
                      {hasFeedItem && !isFeed && (
                        <div className="tagline">{t('rail.addedManually')}</div>
                      )}
                    </span>
                    <span className="amt num">{fmt(p.offer?.value)}</span>
                    <button
                      type="button"
                      className="icon-btn rm-rail"
                      aria-label={t('rail.remove', { name: p.name ?? '' })}
                      disabled={!isFeed && removing}
                      onClick={() => (isFeed ? deselect(p._id) : remove(p._id))}
                    >
                      <IconX />
                    </button>
                  </div>
                )
              })
            )}
          </div>
        )}

        {/* Peek bar — always visible; toggles the item list. */}
        <button
          type="button"
          className="mbasket-peek"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="mbasket-peek-l">
            <IconChevronDown className={`mbasket-chev ${open ? '' : 'up'}`} />
            <span className="mbasket-count">{countLabel}</span>
          </span>
          <span className="mbasket-peek-r">
            <span className="mbasket-k">{t('rail.estimatedReward')}</span>
            <span className="mbasket-reward num">{fmt(reward)}</span>
          </span>
        </button>

        <div className="mbasket-cta">
          {showBack && (
            <button type="button" className="mbasket-back" onClick={onBack}>
              ← {t('rail.back')}
            </button>
          )}
          {exceeded && <div className="rail-warn">{t('rail.maxExceeded', { amount: fmt(max) })}</div>}
          <button
            type="button"
            className="btn btn-primary"
            disabled={continueDisabled}
            onClick={advance}
          >
            {ctaLabel}
            {ctaShowArrow && <IconArrowRight />}
          </button>
        </div>
      </div>
    </div>,
    container,
  )
}
